/**
 * AuthLayout — centered card on cream paper, used by all /auth/* routes.
 *
 * No sidebar, no marketing nav.
 * Hairline card border + brass eyebrow strip at the top + serif italic headline.
 * Footer: single line brand signature.
 *
 * WHITE-LABEL (2026-06-12, Paul PO mandate): when the app is served from a
 * tenant subdomain (firstfirm.elreign.io) the client arriving from their
 * firm's invite email must see the FIRM's identity, not Elreign's — the
 * CSS-var overlay alone (root TenantBrandingOverlay) recolours the chrome but
 * left the mark + footer saying "Elreign", which reads as the wrong company.
 * The layout now resolves the host slug and, when the tenant has published
 * branding, renders the firm logo (or firm-name wordmark) above the card and
 * a "<firm> · client portal powered by Elreign" footer. Same TanStack query
 * key as the root overlay, so this adds no extra network call.
 */

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { ReignMark } from '../reign-mark';
import { FirmLogo } from '../firm-logo';
import { useTenantBrandingBySlug } from '../../lib/api';
import { resolveTenantSlugFromHost } from '../../lib/tenant';
import { warmBackend } from '../../lib/api/warmup';

interface AuthLayoutProps {
  /** Short all-caps brass label rendered above the heading */
  eyebrow?: string;
  /** Serif italic heading. Pass plain text; italics applied in CSS. */
  heading: string;
  /** Secondary line below the heading */
  sub?: ReactNode;
  children: ReactNode;
  /** When true, draws the card border in brass and adds brass-100 tint — signals sensitivity */
  stepUp?: boolean;
}

/**
 * Resolve the firm branding for the current host, or null on the default
 * (app./apex) hosts. Shared by AuthLayout and the auth pages that adjust
 * their copy per-firm (login heading, portal footnote).
 */
export function useAuthHostBranding() {
  const slug =
    typeof window !== 'undefined'
      ? resolveTenantSlugFromHost(
          window.location.host,
          new URLSearchParams(window.location.search),
        )
      : null;
  const query = useTenantBrandingBySlug(slug);
  const branding = query.data?.branding ?? null;
  const firmName = branding?.firmName?.trim() || null;
  // Branded iff the tenant published a firm name — logo/colours are optional.
  return firmName ? { firmName, logoUrl: branding?.logoUrl ?? null } : null;
}

export function AuthLayout({ eyebrow, heading, sub, children, stepUp = false }: AuthLayoutProps) {
  const firm = useAuthHostBranding();

  // Prewarm the cold serverless backend the instant an unauthenticated user
  // lands on ANY auth screen (this layout wraps every /auth/* route — the
  // earliest unauth mount). Login goes straight to Supabase, not our gateway,
  // so without this the user pays the full ~60s APIM+Functions cold-start on
  // the FIRST dashboard data call AFTER they've finished signing in. Fire it
  // once per page load, during the seconds they spend typing credentials/MFA.
  // See lib/api/warmup.ts for why it's fire-and-forget + which URL wakes the FA.
  useEffect(() => {
    warmBackend();
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
      }}
    >
      {/* Mark — the firm's identity on a tenant host, Elreign's otherwise */}
      {firm ? (
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          {(() => {
            // Firm-name wordmark — also the fallback when a broken logo URL
            // fails to load (COMBO-001), so end clients never see a broken
            // image on their firm's auth screen.
            const wordmark = (
              <div
                className="serif"
                // 500 not 600: Newsreader is loaded at 300/400/400-italic/500
                // only (main.tsx), so a 600 here was synthesised faux-bold —
                // a thicker, blurrier stroke than any other serif on the auth
                // screens. Waldo 2026-08-05, "standardize the bold thickness".
                style={{
                  fontSize: 24,
                  fontWeight: 500,
                  // ⚠ THE DERIVED BRAND PRIMARY, NOT THE RAW PICKED HEX
                  // (2026-08-23). This read `--brand-primary`, which the
                  // white-label runtime sets to the firm EXACT input hex, while
                  // every other surface in the app renders the DERIVED,
                  // AA-enforced `--color-brand-primary`. So a firm saw its
                  // wordmark in one shade here and its buttons and links in
                  // another shade everywhere else — two versions of the same
                  // brand on one screen — and a firm that picked a pale colour
                  // got a wordmark that failed contrast on the cream card,
                  // because the raw hex bypasses the contrast pass entirely.
                  // The fallback is the baked Elreign primary rather than ink,
                  // which is correct: a firm that published a name but no
                  // colour IS running on the Elreign palette everywhere else.
                  color: 'var(--color-brand-primary, var(--ink))',
                  letterSpacing: '-0.01em',
                }}
              >
                {firm.firmName}
              </div>
            );
            return firm.logoUrl ? (
              <FirmLogo
                src={firm.logoUrl}
                alt={firm.firmName}
                fallback={wordmark}
                style={{ maxHeight: 40, maxWidth: 240, display: 'block', margin: '0 auto' }}
              />
            ) : (
              wordmark
            );
          })()}
        </div>
      ) : (
        <Link to="/" aria-label="Elreign home" style={{ marginBottom: 32, display: 'block' }}>
          <ReignMark size={32} />
        </Link>
      )}

      {/* Card */}
      <div
        style={{
          width: '100%',
          maxWidth: 440,
          background: stepUp ? 'var(--accent-50)' : 'var(--surface)',
          border: `1px solid ${stepUp ? 'var(--accent-300)' : 'var(--hairline)'}`,
          borderRadius: 'var(--r-xl)',
          boxShadow: '0 1px 0 rgba(20,18,12,0.02), 0 18px 40px -28px rgba(20,18,12,0.14)',
          overflow: 'hidden',
        }}
      >
        {/* Brass eyebrow strip */}
        {eyebrow && (
          <div
            style={{
              background: stepUp ? 'var(--accent-400)' : 'var(--accent-100)',
              borderBottom: `1px solid ${stepUp ? 'var(--accent-300)' : 'var(--hairline)'}`,
              padding: '10px 28px',
            }}
          >
            <span className="eyebrow" style={{ color: stepUp ? 'var(--primary-900)' : undefined }}>
              {eyebrow}
            </span>
          </div>
        )}

        {/* Card body */}
        <div style={{ padding: '32px 28px 28px' }}>
          <h1
            className="serif"
            style={{
              fontSize: 30,
              fontStyle: 'italic',
              color: 'var(--ink)',
              margin: '0 0 6px',
              lineHeight: 1.15,
              letterSpacing: '-0.015em',
            }}
          >
            {heading}
          </h1>
          {sub && (
            <div style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 24, lineHeight: 1.5 }}>
              {sub}
            </div>
          )}
          {!sub && <div style={{ marginBottom: 24 }} />}
          {children}
        </div>
      </div>

      {/* Footer */}
      <p
        style={{
          marginTop: 28,
          fontSize: 12.5,
          color: 'var(--ink-4)',
          textAlign: 'center',
          lineHeight: 1.5,
        }}
      >
        {/* Tagline changed 2026-08-23 (Paul, team walkthrough). It was "Trust
            administration platform that keeps a complete record" — a description
            of the software. The new line is what the software is FOR, said to the
            person reading it. Same string everywhere it appears in the app. */}
        {firm ? (
          <>
            {firm.firmName} &middot; client portal powered by Elreign &middot; Buy
            back your time. Simplify your trust administration.
          </>
        ) : (
          <>Elreign &middot; Buy back your time. Simplify your trust administration.</>
        )}
      </p>
    </div>
  );
}
